import { useRef, useCallback } from 'react';

// Extend Window interface to include dataLayer
declare global {
    interface Window {
        dataLayer: Array<Record<string, any>>;
    }
}

interface TimingEventData {
    [key: string]: string | number | undefined;
}

interface UseSelfScheduleTimerReturn {
    startTimer: (additionalData?: TimingEventData) => void;
    pushTimingEvent: (eventName?: string, additionalData?: TimingEventData) => Promise<void>;
    resetTimer: () => Promise<void>;
}

// Delay in milliseconds to ensure GTM processes events before page redirect
const GTM_EVENT_PROCESSING_DELAY_MS = 100;

// GTM event names for self-schedule timing tracking
export const SELF_SCHEDULE_EVENTS = {
    START: 'self_schedule_start',
    TIMING: 'self_schedule_timing',
    FAILURE: 'self_schedule_failure',
    // "Get A Custom Quote" click on a self-schedule surface (WEBDEV-4259). Its own
    // event so GA4 can count it apart from the See Available Times funnel above.
    CUSTOM_QUOTE: 'self_schedule_custom_quote',
} as const;

/**
 * Pushes one event to the GTM dataLayer and resolves after the same short delay
 * pushTimingEvent uses, so a caller that navigates away immediately afterwards
 * doesn't lose the event to the unload. Resolves without pushing when the
 * dataLayer isn't present (SSR, GTM blocked).
 */
export async function pushDataLayerEvent(eventName: string, data: TimingEventData = {}): Promise<void> {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    try {
        window.dataLayer.push({ event: eventName, ...data });
        await new Promise((resolve) => setTimeout(resolve, GTM_EVENT_PROCESSING_DELAY_MS));
    } catch (error) {
        console.error(`Failed to push ${eventName} event to GTM:`, error);
    }
}

/**
 * Custom hook to track time from self-schedule form submission start to redirect
 * Pushes timing data to GTM dataLayer
 */
export function useSelfScheduleTimer(): UseSelfScheduleTimerReturn {
    const startTimeRef = useRef<number | null>(null);

    /**
     * Start the timer - call when self-schedule form submission begins
     * Also pushes a start event to track abandonment
     * @param additionalData - Any additional data to include in the start event
     */
    const startTimer = useCallback((additionalData: TimingEventData = {}) => {
        startTimeRef.current = Date.now();

        // Push start event to track abandonment (users who close browser during processing)
        if (typeof window !== 'undefined' && window.dataLayer) {
            try {
                window.dataLayer.push({
                    event: SELF_SCHEDULE_EVENTS.START,
                    ...additionalData,
                });
            } catch (error) {
                console.error('Failed to push self_schedule_start event to GTM:', error);
            }
        }
    }, []);

    /**
     * Calculate elapsed time and push to GTM dataLayer
     * Returns a promise that resolves after a small delay to ensure GTM processes the event
     * @param eventName - The GTM event name (default: 'self_schedule_timing')
     * @param additionalData - Any additional data to include in the event
     */
    const pushTimingEvent = useCallback(
        async (eventName: string = SELF_SCHEDULE_EVENTS.TIMING, additionalData: TimingEventData = {}) => {
            if (startTimeRef.current === null) {
                console.warn('useSelfScheduleTimer: Timer was not started before calling pushTimingEvent');
                return;
            }

            const elapsedTime = Date.now() - startTimeRef.current;

            // Push to GTM dataLayer
            if (typeof window !== 'undefined' && window.dataLayer) {
                try {
                    window.dataLayer.push({
                        event: eventName,
                        timing_milliseconds: elapsedTime,
                        ...additionalData,
                    });

                    // Wait briefly to ensure GTM processes the event before page redirect
                    await new Promise((resolve) => setTimeout(resolve, GTM_EVENT_PROCESSING_DELAY_MS));
                } catch (error) {
                    console.error(`Failed to push ${eventName} event to GTM:`, error);
                }
            }

            // Reset the timer
            startTimeRef.current = null;
        },
        []
    );

    /**
     * Reset the timer without pushing an event
     */
    const resetTimer = useCallback(async () => {
        startTimeRef.current = null;
    }, []);

    return {
        startTimer,
        pushTimingEvent,
        resetTimer,
    };
}
